import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  IconButton,
  useTheme,
  Typography,
  Button,
  Stack,
  alpha,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Avatar,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ColorModeContext } from "../../theme";
import { logoutUser } from "../../redux/actions/userActions";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import LoginIcon from "@mui/icons-material/Login";
import MenuIcon from "@mui/icons-material/Menu";
import Logout from "@mui/icons-material/Logout";
import AccountCircle from "@mui/icons-material/AccountCircle";
import { castEnumForDisplay } from "../../helpers/enumHelpers";
import ConfirmDialog from "../common/ConfirmDialog";

const TopBarNavigation = ({ onMobileNavOpen }) => {
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { userInfo } = useSelector((state) => state.user);

  const [time, setTime] = useState(new Date());

  const [anchorEl, setAnchorEl] = useState(null);
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);

  const openMenu = Boolean(anchorEl);

  const padZero = (num) => (num < 10 ? "0" : "") + num;

  const formatTime = () => {
    const hours = time.getHours();
    const minutes = time.getMinutes();
    const seconds = time.getSeconds();
    const meridium = hours >= 12 ? "PM" : "AM";
    return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)} ${meridium}`;
  };

  useEffect(() => {
    const intervalId = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(intervalId);
  }, []);

  const currentDate = new Date().toDateString();

  // Menu Handlers
  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleProfileClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = () => {
    handleProfileClose(); // Close the dropdown menu first
    setIsLogoutDialogOpen(true); // Open the confirmation dialog
  };

  const handleConfirmLogout = () => {
    dispatch(logoutUser());
    navigate("/login");
  };
  return (
    <Box
      component="header"
      sx={{
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        borderBottom: `1px solid ${theme.palette.divider}`,
        position: "relative",
        zIndex: theme.zIndex.sticky,
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex", // Changed from grid to flex for better control with hamburger
          alignItems: "center",
          justifyContent: "space-between",
          p: 1.5,
          minHeight: theme.appSizes.headerHeight,
          gap: 2,
        }}
      >
        {/* --- LEFT SECTION: HAMBURGER & NAME --- */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ flex: 1, minWidth: 0 }}
        >
          {/* Hamburger Icon (Visible only on mobile/tablet) */}
          {userInfo && (
            <IconButton
              onClick={onMobileNavOpen}
              edge="start"
              sx={{
                display: { lg: "none" }, // Hide on large screens where sidebar is permanent
                color: "text.secondary",
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Company Name */}
          <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
            <Typography
              variant="h4"
              noWrap
              sx={{
                fontSize: {
                  xs: "1rem", // Smaller on mobile to fit hamburger
                  sm: "1.25rem",
                  md: theme.typography.h4.fontSize,
                },
                fontWeight: 700,
                color: "text.primary",
              }}
            >
              Fiji Licensing
            </Typography>
          </Box>
        </Stack>

        {/* --- CENTER: DATE & TIME PILL (Hidden on Mobile) --- */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 2,
            pl: 3,
            pr: 0.5,
            py: 0.5,
            borderRadius: "50px",
            bgcolor: alpha(theme.palette.neutral.main, 0.1),
            border: `1px solid ${theme.palette.divider}`,
            position: "absolute", // Absolute centering for perfect alignment
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            {currentDate}
          </Typography>

          <Box
            sx={{
              background: theme.appGradients.info,
              px: 2,
              py: 0.5,
              borderRadius: "50px",
              boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: "#fff", fontWeight: 700 }}
            >
              {formatTime()}
            </Typography>
          </Box>
        </Box>

        {/* --- RIGHT: ACTIONS --- */}
        <Stack
          direction="row"
          spacing={{ xs: 0.5, sm: 1 }}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ flex: 1 }} // Take remaining space
        >
          {/* Theme Toggle (Always Visible) */}
          <IconButton
            onClick={colorMode.toggleColorMode}
            color="inherit"
            size="small"
          >
            {theme.palette.mode === "dark" ? (
              <DarkModeOutlinedIcon />
            ) : (
              <LightModeOutlinedIcon />
            )}
          </IconButton>

          {userInfo ? (
            <>
              {/* Profile Menu Trigger */}
              <IconButton
                onClick={handleProfileClick}
                color={openMenu ? "primary" : "default"}
                aria-controls={openMenu ? "account-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={openMenu ? "true" : undefined}
              >
                {/* Use Avatar if available, else Icon */}
                {userInfo.photoUrl ? (
                  <Avatar
                    src={userInfo.photoUrl}
                    sx={{ width: 32, height: 32 }}
                  />
                ) : (
                  <PersonOutlinedIcon />
                )}
              </IconButton>
            </>
          ) : (
            <>
              <Button
                variant="gradient"
                color="info"
                onClick={() => navigate("/login")}
                sx={{
                  ml: { xs: 1, md: 2 },
                  px: { xs: 2, md: 4 },
                  minWidth: { xs: "auto", md: 64 },
                }}
              >
                <Box
                  component="span"
                  sx={{ display: { xs: "none", md: "block" } }}
                >
                  Login
                </Box>
                <Box
                  component="span"
                  sx={{ display: { xs: "block", md: "none" } }}
                >
                  <LoginIcon fontSize="small" />
                </Box>
              </Button>
            </>
          )}
        </Stack>
      </Box>

      {/* --- PROFILE DROPDOWN MENU --- */}
      {/* This handles Profile, Settings, and Logout cleanly on all devices */}
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={openMenu}
        onClose={handleProfileClose}
        onClick={handleProfileClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            borderRadius: theme.appRadius.md,
            minWidth: 180,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" noWrap>
            {userInfo?.firstName || "User"}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {userInfo ? castEnumForDisplay(userInfo.userRole) : "Staff"}
          </Typography>
        </Box>
        <Divider />

        <MenuItem onClick={() => navigate("/profile")}>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          My Profile
        </MenuItem>
        <MenuItem onClick={() => navigate("/settings")}>
          <ListItemIcon>
            <SettingsOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleLogoutClick} sx={{ color: "error.main" }}>
          <ListItemIcon>
            <Logout fontSize="small" color="error" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={isLogoutDialogOpen}
        onClose={() => setIsLogoutDialogOpen(false)}
        onConfirm={handleConfirmLogout}
        title="Log Out"
        description="Are you sure you want to end your session?"
        severity="error"
        confirmText="Yes, Log Out"
      />
    </Box>
  );
};

export default TopBarNavigation;
